/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

'use strict';
import { Observable, Subject } from 'rxjs';
import { share, publish } from 'rxjs/operators';

import { Ticker } from 'pixi.js-legacy';
import { fastSplice } from 'utils/basic-utils';

//TODO - add a built in frame limiter, execute half as frequently for example by only emitter every other frame

const unZoneInterval = window.__zone_symbol__setInterval;

type RenderCallback = (deltaTime: number) => void;
interface RenderCallbackCollection {
	[key: string]: RenderCallback;
}

class RenderQueue {
	private dedupCollection: RenderCallbackCollection = {};
	private fullCollection: Array<(deltaTime: number) => void> = [];
	private specialBoy: RenderCallbackCollection = {};
	private privileged: { [key: string]: string } = { draw: 'draw' };

	private destroySubject = new Subject();
	private ticker = new Ticker();
	private sharedTicker = Ticker.shared;

	public isRunning = false;
	private shouldRun = false;

	/** an observable that emits when the ticker ticks. You *need* to unsubscribe from this one
	Sample uses:
		`audit(ev => renderQueue.ob$)` - emit the most recent value of the source, allows checking the event
		`sample(renderQueue.ob$),`  - emit the most recent value of the source
	*/
	public ob$: Observable<number>;
	public fakeFrame$: Observable<boolean>;

	constructor() {
		this.ticker.autoStart = true;
		this.ob$ = new Observable((ob) => {
			const listener = (deltaTime: number) => {
				// this.isRunning = true;
				// console.log('diff', performance.now() - window.renderTimestamp);
				// window.renderTimestamp = performance.now();
				ob.next(deltaTime);

				// console.timeEnd("frame ended");
				// this.isRunning = false;
			};
			Ticker.shared.add(listener);
			// let id = unZoneInterval(listener, 16);

			this.destroySubject.subscribe(() => {
				// window.__zone_symbol__clearInterval(id);
				Ticker.shared.remove(listener);
				ob.complete();
			});
		}).pipe(publish<number>());
		(this.ob$ as any).connect();

		this.fakeFrame$ = new Observable((sub) => {
			setInterval(() => {
				sub.next(true);
			}, 16);
		}).pipe(share<boolean>());
	}

	private addTicker() {
		this.ticker.addOnce((deltaTime) => {
			// window.renderTimestamp = performance.now();
			// console.log('renderQueue execute delay', performance.now() - window.renderTimestamp);
			this.execute(deltaTime);
		});
	}

	public push(fn: RenderCallback, key?: string) {
		if (this.isRunning) {
			// console.log('execute immediately', key);
			if (key !== undefined && this.dedupCollection[key]) {
				delete this.dedupCollection[key];
			}
			if (key !== undefined && this.specialBoy[key]) {
				delete this.specialBoy[key];
			}
			fn(0);
			return;
		}

		if (key !== undefined) {
			if (this.privileged[key]) {
				this.specialBoy[key] = fn;
			} else {
				this.dedupCollection[key] = fn;
			}
		} else {
			this.fullCollection.push(fn);
		}
		if (!this.shouldRun) {
			this.addTicker();
		}
		this.shouldRun = true;
	}

	public requestAnimationFramePromise(): Promise<boolean> {
		return new Promise((resolve) => {
			window.requestAnimationFrame(() => {
				resolve(true);
			});
		});
	}

	private execute(deltaTime: number = 0) {
		this.isRunning = true;
		let any = false;
		// console.log('standard execution', this.dedupCollection, this.fullCollection.length);
		for (const key in this.dedupCollection) {
			any = true;
			this.dedupCollection[key](deltaTime);
		}
		if (any) {
			this.dedupCollection = {};
		}

		if (!!this.fullCollection.length) {
			for (let i = 0; i < this.fullCollection.length; i++) {
				this.fullCollection[i](deltaTime);
			}
			fastSplice(this.fullCollection, 0, this.fullCollection.length);
		}

		any = false;
		for (const key in this.specialBoy) {
			any = true;
			this.specialBoy[key](deltaTime);
		}
		if (any) {
			this.specialBoy = {};
		}

		this.shouldRun = false;
		this.ticker.stop();
		this.isRunning = false;
	}
}
export const renderQueue = new RenderQueue();
window.renderQueue = renderQueue;
